import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { useLenis } from "lenis/react";
import { Reveal, Overline } from "../Reveal";
import { PRICING, PRICING_COMPARISON } from "../../data/content";

const Cell = ({ v }) => {
  if (v === true) return <Check size={16} className="text-gk-gold mx-auto" strokeWidth={2.5} />;
  if (v === false) return <Minus size={16} className="text-white/25 mx-auto" />;
  return <span className="text-white/80">{v}</span>;
};

export default function Pricing() {
  const lenis = useLenis();
  const goContact = () => {
    const el = document.getElementById("contact");
    if (el && lenis) lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-16 md:py-24 overflow-hidden">
      <div className="ambient-emerald" style={{ width: 600, height: 400, top: "30%", left: "50%", transform: "translateX(-50%)", opacity: 0.28 }} />

      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-10">
        <Reveal className="text-center mb-10">
          <Overline>Investment</Overline>
          <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-4">
            Packages built for <span className="text-gold-metallic">ambition.</span>
          </h2>
          <p className="font-poppins text-white/55 mt-4 text-sm max-w-xl mx-auto">
            Transparent tiers, uncompromising quality. Every engagement is tailored to your goals.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <motion.div
                data-testid={`pricing-card-${i}`}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`relative h-full rounded-3xl p-6 md:p-7 flex flex-col ${
                  p.featured
                    ? "glass-strong border border-gk-gold/50 shadow-[0_0_60px_rgba(11,61,46,0.4)]"
                    : "luxe-card"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 btn-gold font-inter text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="font-cinzel text-xl text-white">{p.name}</h3>
                <p className="font-poppins text-white/50 text-xs mt-2 min-h-[34px]">{p.tagline}</p>
                <div className="flex items-end gap-1 mt-4 mb-6">
                  <span className={`font-cinzel text-4xl font-bold ${p.featured ? "text-gold-metallic" : "text-white"}`}>{p.price}</span>
                  <span className="font-poppins text-white/40 text-sm mb-2">{p.period}</span>
                </div>
                <div className="hairline-gold w-full mb-5 opacity-50" />
                <ul className="space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 font-poppins text-sm text-white/70">
                      <Check size={16} className="text-gk-champagne shrink-0 mt-0.5" strokeWidth={1.6} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${i}`}
                  onClick={goContact}
                  className={`mt-7 w-full py-3 rounded-full font-inter text-sm font-medium transition-all duration-400 ${
                    p.featured ? "btn-gold" : "btn-glass"
                  }`}
                >
                  {p.name === "Enterprise" ? "Request a Quote" : "Get Started"}
                </button>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Feature comparison */}
        <Reveal delay={0.1} className="mt-14">
          <div className="glass-strong rounded-2xl p-5 md:p-8 overflow-x-auto">
            <table data-testid="pricing-comparison" className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left font-inter text-xs uppercase tracking-widest text-white/40 pb-4">Feature</th>
                  <th className="font-cinzel text-base text-white pb-4">Starter</th>
                  <th className="font-cinzel text-base text-gold-metallic pb-4">Professional</th>
                  <th className="font-cinzel text-base text-white pb-4">Enterprise</th>
                </tr>
              </thead>
              <tbody className="font-poppins text-sm">
                {PRICING_COMPARISON.map((row, i) => (
                  <tr key={i} className="border-b border-white/[0.06]">
                    <td className="py-3.5 text-white/70">{row.feature}</td>
                    <td className="py-3.5 text-center"><Cell v={row.starter} /></td>
                    <td className="py-3.5 text-center bg-white/[0.02]"><Cell v={row.professional} /></td>
                    <td className="py-3.5 text-center"><Cell v={row.enterprise} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
